import React, { useEffect, useState } from "react";
import { Trophy, Crown, Medal } from "lucide-react";
import Seo from "@/components/Seo";
import { AdBanner } from "@/components/AdBanner";
import api from "@/lib/api";
import { GAMES } from "@/data/games";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const RANK_GAMES = GAMES.filter((g) => g.type === "single");

function RankColor(i) {
  if (i === 0) return "#EFFF04";
  if (i === 1) return "#00F0FF";
  if (i === 2) return "#FF003C";
  return "#71717a";
}

function Row({ rank, name, value, suffix }) {
  return (
    <div className="flex items-center gap-4 px-4 py-3 rounded-lg border-2 transition-colors"
      style={{ borderColor: rank < 3 ? RankColor(rank) : "#27272a", background: rank < 3 ? `${RankColor(rank)}10` : "#0a0a0b" }}
      data-testid={`leaderboard-row-${rank}`}>
      <div className="w-9 text-center font-display font-black text-xl" style={{ color: RankColor(rank) }}>
        {rank < 3 ? <Medal className="w-6 h-6 mx-auto" /> : rank + 1}
      </div>
      <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-black" style={{ background: RankColor(rank) }}>
        {name[0]?.toUpperCase()}
      </div>
      <span className="flex-1 font-semibold truncate">{name}</span>
      <span className="font-mono text-neon-cyan font-bold">{value}{suffix}</span>
    </div>
  );
}

export default function Leaderboard() {
  const [global, setGlobal] = useState([]);
  const [gameRows, setGameRows] = useState({});
  const [tab, setTab] = useState("global");

  useEffect(() => {
    api.get("/leaderboard").then(({ data }) => setGlobal(data)).catch(() => {});
  }, []);

  const loadGame = async (gid) => {
    if (gameRows[gid]) return;
    try { const { data } = await api.get(`/scores/${gid}`); setGameRows((p) => ({ ...p, [gid]: data })); }
    catch { setGameRows((p) => ({ ...p, [gid]: [] })); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-12" data-testid="leaderboard-page">
      <Seo title="Leaderboard" description="See the top players on Video Game Mania. Compete for the highest scores across all arcade games and brain teasers." />
      <div className="flex items-center gap-3 mb-2">
        <Trophy className="w-9 h-9 text-neon-yellow" />
        <h1 className="font-display font-black text-4xl md:text-5xl uppercase tracking-tighter">Leaderboard</h1>
      </div>
      <p className="text-zinc-400 mb-8">The best of the best. Log in and play to claim your spot.</p>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v !== "global") loadGame(v); }}>
        <TabsList className="bg-zinc-900 border border-zinc-800 flex-wrap h-auto">
          <TabsTrigger value="global" data-testid="tab-global" className="data-[state=active]:bg-neon-cyan data-[state=active]:text-black uppercase text-xs font-bold">
            <Crown className="w-3 h-3 mr-1" /> Overall
          </TabsTrigger>
          {RANK_GAMES.map((g) => (
            <TabsTrigger key={g.id} value={g.id} data-testid={`tab-${g.id}`}
              className="data-[state=active]:bg-neon-cyan data-[state=active]:text-black uppercase text-xs font-bold">
              {g.title}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="global" className="space-y-2 mt-6">
          {global.length === 0 ? (
            <p className="text-zinc-500 text-center py-12 font-mono">No scores yet. Be the first to play!</p>
          ) : global.map((r, i) => <Row key={r.user_id} rank={i} name={r.username} value={r.total} suffix=" pts" />)}
        </TabsContent>

        {RANK_GAMES.map((g) => (
          <TabsContent key={g.id} value={g.id} className="space-y-2 mt-6">
            {(gameRows[g.id] || []).length === 0 ? (
              <p className="text-zinc-500 text-center py-12 font-mono">No scores yet for {g.title}.</p>
            ) : gameRows[g.id].map((r, i) => <Row key={r.user_id} rank={i} name={r.username} value={r.score} suffix="" />)}
          </TabsContent>
        ))}
      </Tabs>

      <AdBanner slot="leaderboard" className="mt-10" />
    </div>
  );
}
